import {Group, GroupMember, GroupMemberRoleType, TourRoleType} from '../../../../graphql/groups.datatypes';
import {makeStyles} from '@material-ui/styles';
import React from 'react';
import {Card, CardContent, CardHeader, colors, Divider, Grid, Theme, Typography} from '@material-ui/core';
import {formatDate} from '../../../../utils/formatters';
import {getBlankGroup} from '../../GroupDetails/GroupDetails';
import getGroupWithQuery, { getGroupOverviewQuery } from '../../getGroupWithQuery.api';
import {gqlGroupMember} from '../../getGroupMember.api';
import {
    gqlGroupForGroupOverviewCardQuery
} from '../../getGroupQueries.api';
import {T} from 'react-polyglot-hooks';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
}));


const GroupOverviewCard = (props: { group: Group, showPrices: boolean, isAdmin: boolean }) => {
    const {group, showPrices, isAdmin} = props;
    const isNewGroup = group.id === -1;
    const groupForGroupOverviewCardData = isNewGroup ? {loading: false, data: {getGroupOverview: getBlankGroup()}, error: null} : getGroupOverviewQuery(Number(group.id), gqlGroupForGroupOverviewCardQuery);
    const groupForGroupOverviewCard = groupForGroupOverviewCardData && !groupForGroupOverviewCardData.loading && groupForGroupOverviewCardData.data && groupForGroupOverviewCardData.data.getGroupOverview;
    const classes = useStyles();
    const primaryLeader = groupForGroupOverviewCard && groupForGroupOverviewCard.primaryLeader;
    const additionalLeaders = groupForGroupOverviewCard && groupForGroupOverviewCard.members && groupForGroupOverviewCard.members.filter(member => member.group_role === GroupMemberRoleType.LEADER);
    const formatLeader = (member?: GroupMember) => member ? (<React.Fragment key={member.id}>{member.first_name} {member.last_name} ({member.email})<br/></React.Fragment>) : 'No Leader Set';
    const ratio = groupForGroupOverviewCard && groupForGroupOverviewCard.comp_ratio ? groupForGroupOverviewCard.comp_ratio + 1 : 1;
    const estimate = groupForGroupOverviewCard && Math.floor((groupForGroupOverviewCard.members_count || 0) / ratio) * groupForGroupOverviewCard.comp_dollar_value!;
    const totalMembers = groupForGroupOverviewCard && groupForGroupOverviewCard.members_count ? groupForGroupOverviewCard.members_count : 0;
    const cancelledMembers = groupForGroupOverviewCard && groupForGroupOverviewCard.cancelled_members_count ? groupForGroupOverviewCard.cancelled_members_count : 0;
    const activeMembers = totalMembers - cancelledMembers;

    return (
        <React.Fragment>
            <Card className={classes.container}>
                <CardHeader
                    title={'Group Overview'}
                />
                <Divider/>
                <CardContent className={classes.card}>
                    <Grid container alignItems="center" spacing={1}>
                        {groupForGroupOverviewCardData.loading && (
                            <React.Fragment>
                                <Grid item xs={4}>
                                    <Typography><T phrase="Loading..."/></Typography>
                                </Grid>
                            </React.Fragment>
                        )}
                        {groupForGroupOverviewCard && !groupForGroupOverviewCardData.loading && (
                            <React.Fragment>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Group Name:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{groupForGroupOverviewCard.name ? groupForGroupOverviewCard.name : '-'}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Tour Name:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{isAdmin ? ((groupForGroupOverviewCard.tour!.internal_name) ? groupForGroupOverviewCard.tour!.internal_name : '-') : ((groupForGroupOverviewCard.tour!.display_name) ? groupForGroupOverviewCard.tour!.display_name : '-')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Hotel Name:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{isAdmin ? ((groupForGroupOverviewCard.hotel!.internal_name) ? groupForGroupOverviewCard.hotel!.internal_name : '-') : ((groupForGroupOverviewCard.hotel!.display_name) ? groupForGroupOverviewCard.hotel!.display_name : '-')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Start Date:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{((groupForGroupOverviewCard.start_date) ? formatDate(groupForGroupOverviewCard.start_date) : '-')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">End Date:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{((groupForGroupOverviewCard.end_date) ? formatDate(groupForGroupOverviewCard.end_date) : '-')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Cutoff Date:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{((groupForGroupOverviewCard.admissions_cutoff) ? formatDate(groupForGroupOverviewCard.admissions_cutoff) : '-')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Primary Leader:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{((primaryLeader) ? formatLeader(primaryLeader) : '-')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Additional Leaders:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{!additionalLeaders || additionalLeaders.length === 0 ? 'No Leader Set' : additionalLeaders.map(formatLeader)}</Typography>
                                </Grid>
                                {showPrices && (
                                    <React.Fragment>
                                        <Grid item xs={4}>
                                            <Typography variant="body1">Convenience Fee:</Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body1">{((groupForGroupOverviewCard.convenience_fee) ? '$' + groupForGroupOverviewCard.convenience_fee : '-')}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body1">Comp Ratio:</Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body1">{((groupForGroupOverviewCard.comp_ratio) ? '1/' + groupForGroupOverviewCard.comp_ratio : '-')}</Typography>
                                        </Grid>
                                        {/*<Grid item xs={4}>*/}
                                        {/*    <Typography variant="body1">Comp $ Estimate:</Typography>*/}
                                        {/*</Grid>*/}
                                        {/*<Grid item xs={8}>*/}
                                        {/*    <Typography variant="body1">{((estimate) ? '$' + estimate : '-')}</Typography>*/}
                                        {/*</Grid>*/}
                                    </React.Fragment>
                                )}

                                <Grid item xs={4}>
                                    <Typography variant="body1">Total # of active members</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{activeMembers}</Typography>
                                </Grid>
                                {/*<Grid item xs={4}>*/}
                                {/*    <Typography variant="body1">Total # of performers:</Typography>*/}
                                {/*</Grid>*/}
                                {/*<Grid item xs={8}>*/}
                                {/*    <Typography variant="body1">{((groupForGroupOverviewCard.performers_count) ? groupForGroupOverviewCard.performers_count : 0)}</Typography>*/}
                                {/*</Grid>*/}
                                <Grid item xs={4}>
                                    <Typography variant="body1">Total # of cancelled members:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{cancelledMembers}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Group Paid in full?:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{((groupForGroupOverviewCard.outstandingBalance! > 0) ? 'No' : 'Yes')}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Group Missing Mandatory Docs?:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body1">{((groupForGroupOverviewCard.outstandingBalance! > 0) ? 'No' : 'Yes')}</Typography>
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                </CardContent>
            </Card>
        </React.Fragment>
    );
};

export default GroupOverviewCard;
